// Submit logic for the /create studio. The request body, the optimistic
// credit update and the error -> paywall mapping all come from
// components/modals/conversionRequest.js, the same module CreateModal uses,
// so the backend sees an identical POST /api/v2/conversion either way.
//
// No plan and no credits: the server saves a locked cover (nothing runs)
// and its page plays the setup sequence and the unlock paywall, so the
// paywall meets them after they've made something.
import { useEffect, useRef, useState } from "react";
import { useRouter } from "next/router";
import toast from "react-hot-toast";
import { useAuth } from "@/context/AuthProvider";
import { useModal } from "@/context/ModalContext";
import useAuthModal from "@/hooks/useAuthModal";
import useSubscription from "@/hooks/useSubscription";
import { track } from "@/utils/analytics";
import {
  getConversionName,
  getOptimisticUserUpdate,
  handleConversionErrorMessage,
} from "@/components/modals/conversionRequest";
import { useT } from "@jammable/ui-core/src/i18n";
import { postCover } from "./coverRequest";

/* eslint-disable @typescript-eslint/no-explicit-any */

export interface CreateCoverInput {
  selectedVoices: any[];
  selectedSongsList: any[];
  settings: any;
  marbleRun?: any;
  /** Who sings when, if they chose it (WhoSings). */
  voiceTimes?: { voice: string; start: number; end: number }[];
}

export default function useCreateCover() {
  const router = useRouter();
  const t = useT();
  const { user, updateUser } = useAuth();
  const { openModal } = useModal();
  const { checkAuthAndExecute } = useAuthModal();
  const { hasCreatorPlan, hasPowerPlan } = useSubscription();
  const [loading, setLoading] = useState(false);

  // The sign-in modal resumes with a continuation. Point it at a ref so the
  // resumed call sees the latest user/quota, not the closure from before
  // sign-in. Choices live in the page's state and survive the modal.
  const latest = useRef<(input: CreateCoverInput) => Promise<void>>(
    async () => {},
  );

  const submit = async ({
    selectedVoices,
    selectedSongsList,
    settings,
    marbleRun,
    voiceTimes,
  }: CreateCoverInput) => {
    if (
      loading ||
      !selectedSongsList ||
      selectedSongsList.length === 0 ||
      selectedVoices.length === 0
    ) {
      return;
    }

    // Logged out -> the existing sign-in modal; creation resumes after it.
    if (
      !checkAuthAndExecute(
        () =>
          latest.current({
            selectedVoices,
            selectedSongsList,
            settings,
            marbleRun,
            voiceTimes,
          }),
        t("studio.auth.save"),
      )
    ) {
      return;
    }

    const conversionName = getConversionName(settings, selectedSongsList);

    setLoading(true);
    try {
      const { shortid, locked } = await postCover({
        selectedVoices,
        selectedSongsList,
        settings,
        marbleRun,
        voiceTimes,
      });
      if (user?.quota && !locked) {
        updateUser(
          getOptimisticUserUpdate(
            user,
            marbleRun ? { template: marbleRun } : null,
          ),
        );
      }
      if (locked) {
        track("Locked Cover", {
          voiceIds: selectedVoices?.map((v) => v._id),
          sourceModes: selectedSongsList?.map((s) => s.sourceMode),
          from: "studio",
        });
      }
      router.push(`/conversion/${shortid}`);
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message;
      track("Abandoned Conversion", {
        reason: errorMessage,
        voiceIds: selectedVoices?.map((v) => v._id),
        voiceNames: selectedVoices?.map((v) => v.artistname),
        songInput: selectedSongsList?.[0]?.url,
        conversionName,
        from: "studio",
      });
      handleConversionErrorMessage({
        errorMessage,
        openModal,
        hasCreatorOrPowerPlan: hasCreatorPlan() || hasPowerPlan(),
        toastError: toast.error,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    latest.current = submit;
  });

  return { submit, loading };
}
